import React, { useState, useEffect, useCallback, useRef } from "react";
import useApi from "@/hooks/useApi";

interface LimitItem {
  name: string;
  current: number;
  max: number;
}

interface PlanSummary {
  packageName: string;
  enabledChannels: string[];
  enabledFeatures: string[];
  enabledIntegrations: string[];
  enabledSecurity: string[];
  limits: LimitItem[];
}

const BADGE_COLORS: Record<string, string> = {
  channels: "bg-primary-bg text-primary-active",
  features: "bg-grape-100 text-grape-700",
  integrations: "bg-sweet-orange-100 text-sweet-orange-700",
  security: "bg-crimson-red-100 text-crimson-red-700",
};

export default function PlanFeaturesPanel() {
  const api = useApi();
  const [plan, setPlan] = useState<PlanSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showGuide, setShowGuide] = useState(false);
  const isFirstLoad = useRef(true);

  const fetchPlan = useCallback(async () => {
    if (isFirstLoad.current) setLoading(true);
    try {
      const resp = await api.messaging.get("admin/billing/plan-summary");
      if (resp.ok) {
        const data = await resp.json();
        setPlan(data);
        setError(null);
      } else {
        setError("Failed to load plan details.");
      }
    } catch {
      setError("Failed to connect to server.");
    } finally {
      setLoading(false);
      isFirstLoad.current = false;
    }
  }, [api.messaging]);

  useEffect(() => {
    fetchPlan();
    const interval = setInterval(fetchPlan, 60000);
    return () => clearInterval(interval);
  }, [fetchPlan]);

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-crimson-red-50 border border-crimson-red-200 rounded-lg p-4 text-sm text-crimson-red-700">
          {error}
          <button
            onClick={fetchPlan}
            className="ml-3 text-primary hover:text-primary-active font-medium"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!plan) return null;

  const channels = Array.isArray(plan.enabledChannels) ? plan.enabledChannels : [];
  const features = Array.isArray(plan.enabledFeatures) ? plan.enabledFeatures : [];
  const integrations = Array.isArray(plan.enabledIntegrations) ? plan.enabledIntegrations : [];
  const security = Array.isArray(plan.enabledSecurity) ? plan.enabledSecurity : [];
  const limits = Array.isArray(plan.limits) ? plan.limits : [];

  return (
    <div className="p-6 space-y-6">
      {/* Plan Header */}
      <div className="bg-white rounded-lg border border-slate-gray-200 p-5">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-base font-semibold text-slate-gray-800">Current Plan</h3>
            <p className="text-2xl font-bold text-primary mt-1">
              {plan.packageName ?? "Custom"}
            </p>
          </div>
          <span className="inline-flex px-3 py-1 rounded-full text-xs font-medium bg-emerald-green-50 text-emerald-green-700">
            Active
          </span>
        </div>
      </div>

      {/* Enabled Channels */}
      <FeatureSection
        title="Channels"
        items={channels}
        badgeColor={BADGE_COLORS.channels}
        emptyText="No channels enabled"
      />

      {/* Enabled Features */}
      <FeatureSection
        title="Features"
        items={features}
        badgeColor={BADGE_COLORS.features}
        emptyText="No features enabled"
      />

      {/* Integrations */}
      {integrations.length > 0 && (
        <FeatureSection
          title="Integrations"
          items={integrations}
          badgeColor={BADGE_COLORS.integrations}
          emptyText="No integrations enabled"
        />
      )}

      {/* Security */}
      {security.length > 0 && (
        <FeatureSection
          title="Security"
          items={security}
          badgeColor={BADGE_COLORS.security}
          emptyText="No security features enabled"
        />
      )}

      {/* Limits Table */}
      <div className="bg-white rounded-lg border border-slate-gray-200 p-5">
        <h3 className="text-base font-semibold text-slate-gray-800 mb-4">Plan Limits</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-gray-200">
                <th className="text-left py-2 px-3 text-slate-gray-500 font-medium">Resource</th>
                <th className="text-right py-2 px-3 text-slate-gray-500 font-medium">Limit</th>
              </tr>
            </thead>
            <tbody>
              {limits.map((lim, i) => (
                <tr key={i} className="border-b border-slate-gray-100 last:border-0">
                  <td className="py-2 px-3 text-slate-gray-700">{lim.name}</td>
                  <td className="py-2 px-3 text-right font-mono text-slate-gray-600">
                    {lim.max <= 0 ? (
                      <span className="text-primary">Unlimited</span>
                    ) : (
                      lim.max.toLocaleString()
                    )}
                  </td>
                </tr>
              ))}
              {limits.length === 0 && (
                <tr>
                  <td colSpan={2} className="py-4 text-center text-slate-gray-400">
                    No limit information available.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Help Guide (expandable) */}
      <div className="bg-white rounded-lg border border-slate-gray-200">
        <button
          onClick={() => setShowGuide(!showGuide)}
          className="w-full flex items-center justify-between p-5 text-left"
          aria-expanded={showGuide}
        >
          <h3 className="text-base font-semibold text-slate-gray-800">Understanding Your Plan</h3>
          <svg
            className={`w-5 h-5 text-slate-gray-400 transition-transform ${showGuide ? "rotate-180" : ""}`}
            fill="none" viewBox="0 0 24 24" stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        {showGuide && (
          <div className="px-5 pb-5 space-y-4 text-sm text-slate-gray-600">
            <div>
              <h4 className="font-semibold text-slate-gray-700 mb-1">Channels</h4>
              <p>Communication channels determine how customers can reach you. Each enabled channel allows conversations through that medium (web chat, SMS, email, etc.).</p>
            </div>
            <div>
              <h4 className="font-semibold text-slate-gray-700 mb-1">Monthly Limits</h4>
              <p>Some resources reset on the 1st of each month (conversations, SMS, IVR minutes, report exports). Usage counters start fresh each billing cycle.</p>
            </div>
            <div>
              <h4 className="font-semibold text-slate-gray-700 mb-1">Resource Limits</h4>
              <p>Static limits (users, custom fields, knowledge indexes) represent the maximum number of items you can create. These do not reset monthly.</p>
            </div>
            <div>
              <h4 className="font-semibold text-slate-gray-700 mb-1">What counts as a page?</h4>
              <p>
                For uploaded files (PDF, DOCX, XLSX): 1 page = 1 document page as reported by the file.
                For web scraper: 1 page = 1 URL crawled. Your monthly page budget covers both.
                The &ldquo;Max Pages Per File&rdquo; limit caps how many pages are indexed from a single file — pages beyond that limit are not indexed.
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-slate-gray-700 mb-1">Unlimited Resources</h4>
              <p>Resources showing &quot;Unlimited&quot; have no cap on your current plan. A value of 0 for a monthly limit means unlimited usage.</p>
            </div>
            <div>
              <h4 className="font-semibold text-slate-gray-700 mb-1">Need More?</h4>
              <p>Contact your account manager to discuss upgrading your plan or adding capacity for specific resources.</p>
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="text-center text-sm text-slate-gray-400 py-2">
        Contact your account manager to change your plan or request additional capacity.
      </div>
    </div>
  );
}

function FeatureSection({
  title,
  items,
  badgeColor,
  emptyText
}: {
  title: string;
  items: string[];
  badgeColor: string;
  emptyText: string;
}) {
  return (
    <div className="bg-white rounded-lg border border-slate-gray-200 p-5">
      <h3 className="text-base font-semibold text-slate-gray-800 mb-3">{title}</h3>
      {items.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {items.map(item => (
            <span
              key={item}
              className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${badgeColor}`}
            >
              {item}
            </span>
          ))}
        </div>
      ) : (
        <p className="text-sm text-slate-gray-400">{emptyText}</p>
      )}
    </div>
  );
}
